import { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useInviteTeamMember } from '@/hooks/useTeamManagement';
import { useRoleDefinitions } from '@/hooks/usePermissions';
import { Eye, EyeOff, Mail, User, Building2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InviteUserDialog({ open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [roleSlug, setRoleSlug] = useState('');
  const [department, setDepartment] = useState('');
  const [tempPassword, setTempPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  const invite = useInviteTeamMember();
  const { data: roles } = useRoleDefinitions();

  // Filter roles: can't assign owner unless you are owner
  const availableRoles = roles?.filter(r => {
    if (r.slug === 'org_owner') return false; // Only owners create owners via special flow
    return true;
  }) || [];

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
    let pwd = '';
    for (let i = 0; i < 12; i++) pwd += chars[Math.floor(Math.random() * chars.length)];
    setTempPassword(pwd);
  };

  const hasDraft = !!email.trim() ||
    !!fullName.trim() ||
    !!roleSlug ||
    !!department.trim() ||
    !!tempPassword;

  const resetDraft = () => {
    setEmail('');
    setFullName('');
    setRoleSlug('');
    setDepartment('');
    setTempPassword('');
    setShowPassword(false);
  };

  const requestClose = () => {
    if (invite.isPending) return;
    if (hasDraft) {
      setDiscardConfirmOpen(true);
      return;
    }
    onOpenChange(false);
  };

  const discardAndClose = () => {
    resetDraft();
    setDiscardConfirmOpen(false);
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    if (!email || !fullName || !roleSlug || !tempPassword) return;
    await invite.mutateAsync({
      email,
      full_name: fullName,
      role_slug: roleSlug,
      department: department || undefined,
      temp_password: tempPassword,
    });
    resetDraft();
    onOpenChange(false);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(nextOpen) => { if (nextOpen) onOpenChange(true); else requestClose(); }}>
      <DialogContent className="rounded-xl max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">{t('hire.team.inviteUser.title')}</DialogTitle>
          <DialogDescription>
            {t('hire.team.inviteUser.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">{t('hire.account.fullName')}</label>
            <div className="relative">
              <User className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder={t('hire.team.inviteUser.fullNamePlaceholder')}
                className="ps-9 h-10 rounded-xl"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">{t('hire.settings.loginEmail')}</label>
            <div className="relative">
              <Mail className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('hire.team.inviteUser.emailPlaceholder')}
                className="ps-9 h-10 rounded-xl"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">{t('hire.team.usersList.columns.role')}</label>
            <Select value={roleSlug} onValueChange={setRoleSlug}>
              <SelectTrigger className="h-10 rounded-xl">
                <SelectValue placeholder={t('hire.team.inviteUser.selectRole')} />
              </SelectTrigger>
              <SelectContent>
                {availableRoles.map((r) => (
                  <SelectItem key={r.slug} value={r.slug}>
                    <div>
                      <span className="font-medium">{t(`hire.roles.slugLabels.${r.slug}`, { defaultValue: r.name })}</span>
                      {r.description && (
                        <span className="text-xs text-muted-foreground ms-2">— {r.description}</span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">{t('hire.team.usersList.columns.department')} <span className="text-muted-foreground">{t('hire.team.inviteUser.optional')}</span></label>
            <div className="relative">
              <Building2 className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                placeholder={t('hire.team.inviteUser.departmentPlaceholder')}
                className="ps-9 h-10 rounded-xl"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">{t('hire.team.inviteUser.temporaryPassword')}</label>
              <Button type="button" variant="ghost" size="sm" className="text-xs h-7" onClick={generatePassword}>
                {t('hire.team.inviteUser.generate')}
              </Button>
            </div>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={tempPassword}
                onChange={(e) => setTempPassword(e.target.value)}
                placeholder={t('hire.team.inviteUser.passwordPlaceholder')}
                className="pe-10 h-10 rounded-xl"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">{t('hire.team.inviteUser.passwordHint')}</p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-xl" onClick={requestClose}>
            {t('common.cancel')}
          </Button>
          <Button
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl"
            onClick={handleSubmit}
            disabled={invite.isPending || !email || !fullName || !roleSlug || !tempPassword}
          >
            {invite.isPending ? t('hire.team.inviteUser.inviting') : t('hire.team.usersList.inviteMember')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <AlertDialog open={discardConfirmOpen} onOpenChange={setDiscardConfirmOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('hire.team.inviteUser.discardDialog.title')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('hire.team.inviteUser.discardDialog.description')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('hire.team.inviteUser.discardDialog.keepEditing')}</AlertDialogCancel>
          <AlertDialogAction onClick={discardAndClose}>
            {t('hire.team.inviteUser.discardDialog.discard')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
